import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { 
  Plane, 
  Hotel as HotelIcon, 
  Car, 
  Utensils, 
  MapPin, 
  Clock, 
  DollarSign,
  ChevronRight,
  ShoppingCart,
  Check
} from 'lucide-react'
import type { ItinerarySegment, FlightResult, Hotel } from "@/types/travel"
import { useCart } from "@/lib/cart"
import { formatCurrency } from "@/lib/utils"
import { cn } from "@/lib/utils"

interface ItinerarySegmentCardProps {
  segment: ItinerarySegment
  availableFlights?: FlightResult[]
  availableHotels?: Hotel[]
  onSelectItem?: (itemId: string, type: 'flight' | 'hotel') => void
  hideTimeBadge?: boolean // Hide time badge when grouped
}

const typeIcons = {
  flight: Plane,
  hotel: HotelIcon,
  transport: Car,
  meal: Utensils,
  activity: MapPin,
  accommodation: HotelIcon
}

const timeLabels = {
  arrival: 'Arrival',
  morning: 'Morning',
  afternoon: 'Afternoon', 
  evening: 'Evening',
  night: 'Night',
  departure: 'Departure'
}

const timeColors = {
  arrival: 'bg-blue-50 text-blue-900 border border-blue-200 dark:bg-blue-950 dark:text-blue-100',
  morning: 'bg-sky-50 text-sky-900 border border-sky-200 dark:bg-sky-950 dark:text-sky-100',
  afternoon: 'bg-teal-50 text-teal-900 border border-teal-200 dark:bg-teal-950 dark:text-teal-100',
  evening: 'bg-indigo-50 text-indigo-900 border border-indigo-200 dark:bg-indigo-950 dark:text-indigo-100',
  night: 'bg-violet-50 text-violet-900 border border-violet-200 dark:bg-violet-950 dark:text-violet-100',
  departure: 'bg-slate-50 text-slate-900 border border-slate-200 dark:bg-zinc-950 dark:text-gray-100'
}

export function ItinerarySegmentCard({ 
  segment, 
  availableFlights, 
  availableHotels,
  onSelectItem,
  hideTimeBadge = false
}: ItinerarySegmentCardProps) {
  const { addToCart, cart } = useCart()
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  
  const Icon = typeIcons[segment.type] || MapPin
  const isBookable = segment.bookingRequired && (segment.linkedResultIds?.length || segment.linkedResultId)
  
  // Get linked items based on segment type
  const linkedItems = (() => {
    if (!isBookable) return []
    
    if (segment.type === 'flight' && availableFlights) {
      const ids = segment.linkedResultIds || (segment.linkedResultId ? [segment.linkedResultId] : [])
      return availableFlights.filter(f => ids.includes(f.id))
    }
    
    if ((segment.type === 'hotel' || segment.type === 'accommodation') && availableHotels) {
      const ids = segment.linkedResultIds || (segment.linkedResultId ? [segment.linkedResultId] : [])
      return availableHotels.filter(h => ids.includes(h.id))
    }
    
    return []
  })()
  
  const isInCart = selectedItemId ? 
    cart.items.some(item => item.id === selectedItemId) : false
  
  const handleAddToCart = (item: FlightResult | Hotel) => {
    const type = 'price' in item && 'flightNumber' in item ? 'flight' : 'hotel'
    addToCart(item as any, type)
    setSelectedItemId(item.id)
    if (onSelectItem) {
      onSelectItem(item.id, type)
    }
  }
  
  return (
    <Card className={cn(
      "transition-all duration-200",
      "bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border border-white/30 dark:border-zinc-700/50",
      isBookable && "hover:shadow-lg hover:bg-white/20 dark:hover:bg-zinc-700/60 hover:border-primary/30 dark:hover:border-blue-500/50"
    )}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Time Badge - Only show if not grouped */}
          {!hideTimeBadge && (
            <Badge 
              variant="secondary"
              className={cn("min-w-[80px] justify-center rounded-full", timeColors[segment.time])}
            >
              {timeLabels[segment.time]}
            </Badge>
          )}
          
          {/* Icon */}
          <div className={cn(
            "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center",
            isBookable ? "bg-primary/10 text-primary" : "bg-muted"
          )}>
            <Icon className="h-5 w-5" />
          </div>
          
          {/* Content */}
          <div className="flex-1 space-y-2">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium text-sm">{segment.suggestion}</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  {segment.description}
                </p>
              </div>
              
              {/* Action Button */}
              {isBookable && linkedItems.length > 0 && (
                <Button
                  variant={isInCart ? "default" : "outline"}
                  size="sm"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="ml-2 rounded-full"
                >
                  {isInCart ? (
                    <>
                      <Check className="h-3 w-3 mr-1" />
                      Selected
                    </>
                  ) : (
                    <>
                      Select
                      <ChevronRight className={cn(
                        "h-3 w-3 ml-1 transition-transform",
                        isExpanded && "rotate-90"
                      )} />
                    </>
                  )}
                </Button>
              )}
            </div>
            
            {/* Metadata */}
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              {segment.duration && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {segment.duration}
                </div>
              )}
              {segment.estimatedCost && (
                <div className="flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  {formatCurrency(segment.estimatedCost)}
                </div>
              )}
            </div>
            
            {/* Alternatives */}
            {segment.alternatives && segment.alternatives.length > 0 && (
              <div className="mt-2 pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-1">Alternatives:</p>
                <div className="flex flex-wrap gap-1">
                  {segment.alternatives.map((alt, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs rounded-full">
                      {alt}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Expanded Options */}
            {isExpanded && linkedItems.length > 0 && (
              <div className="mt-3 space-y-2 animate-in slide-in-from-top-2">
                <p className="text-xs text-muted-foreground font-medium">
                  Select from {linkedItems.length} available option{linkedItems.length > 1 ? 's' : ''}:
                </p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {linkedItems.map((item) => {
                    const isSelected = item.id === selectedItemId
                    const isFlightItem = 'flightNumber' in item
                    
                    return (
                      <Card 
                        key={item.id}
                        className={cn(
                          "p-3 cursor-pointer transition-all",
                          isSelected && "border-primary bg-primary/5"
                        )}
                        onClick={() => handleAddToCart(item)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            {isFlightItem ? (
                              <div className="text-sm">
                                <p className="font-medium">
                                  {(item as any).airline} {(item as any).flightNumber}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {(item as any).origin} → {(item as any).destination}
                                </p>
                              </div>
                            ) : (
                              <div className="text-sm">
                                <p className="font-medium">{(item as Hotel).name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {(item as Hotel).location}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {formatCurrency((item as any).price)}
                            </span>
                            {isSelected ? (
                              <Button size="sm" variant="default" disabled className="rounded-full">
                                <Check className="h-3 w-3" />
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" className="rounded-full">
                                <ShoppingCart className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}